import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
type Section = {
  heading: string;
  body: string[];
};
type LegalPageProps = {
  title: string;
  lastUpdated: string;
  intro: string;
  sections: Section[];
};
export function LegalPage({
  title,
  lastUpdated,
  intro,
  sections
}: LegalPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-cream" data-id="element-111">
      {/* Minimal header */}
      <header className="border-b border-navy/10 bg-cream/90 backdrop-blur-md sticky top-0 z-50" data-id="element-112">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between" data-id="element-113">
          <Link to="/" className="flex items-center gap-2" data-id="element-114">
            <img src="https://cdn.magicpatterns.com/uploads/uJ1Trq7SuNbdwizbCqPRU7/launchcave_logo.png" alt="LaunchCave Logo" className="h-12 md:h-14 w-auto object-contain" data-id="element-115" />
          </Link>
          <Link to="/" className="group inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-orange transition-colors" data-id="element-116">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-117" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="px-6 md:px-12 py-20 md:py-32" data-id="element-118">
        <div className="max-w-3xl mx-auto" data-id="element-119">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-120">
            <p className="text-sm font-semibold tracking-widest uppercase text-orange mb-6" data-id="element-121">
              Legal
            </p>
            <h1 className="text-5xl md:text-7xl font-serif text-navy text-balance leading-[1.05] mb-6" data-id="element-122">
              {title}
            </h1>
            <p className="text-sm text-navy/50 mb-12" data-id="element-123">
              Last updated: {lastUpdated}
            </p>

            <p className="text-lg md:text-xl text-navy/80 leading-relaxed font-light mb-16 pb-16 border-b border-navy/10" data-id="element-124">
              {intro}
            </p>

            <div className="space-y-14" data-id="element-125">
              {sections.map((section, index) => <motion.section key={section.heading} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.05
            }} data-id="element-126">
                  <h2 className="text-2xl md:text-3xl font-serif text-navy mb-6" data-id="element-127">
                    {index + 1}. {section.heading}
                  </h2>
                  <div className="space-y-4" data-id="element-128">
                    {section.body.map((paragraph, i) => <p key={i} className="text-base md:text-lg text-navy/75 font-light leading-relaxed" data-id="element-129">
                        {paragraph}
                      </p>)}
                  </div>
                </motion.section>)}
            </div>

            <div className="mt-24 pt-12 border-t border-navy/10" data-id="element-130">
              <p className="text-sm text-navy/60 mb-4" data-id="element-131">
                Questions about this document?
              </p>
              <a href="mailto:hello@launchcave.com" className="text-orange hover:text-navy transition-colors text-lg" data-id="element-132">
                hello@launchcave.com
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="bg-navy text-cream/60 py-10 px-6 md:px-12 text-sm" data-id="element-133">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-134">
          <p data-id="element-135">
            © {new Date().getFullYear()} LaunchCave LLC. All rights reserved.
          </p>
          <div className="flex gap-6" data-id="element-136">
            <Link to="/privacy" className="hover:text-cream transition-colors" data-id="element-137">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-cream transition-colors" data-id="element-138">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>;
}